import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);
const PREVIEW_KEY = "dok_admin_preview_role";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Admin Preview Mode — when true, user.role is locally overridden to "player"
    // so the entire app behaves like a regular user. Original role kept in sessionStorage.
    const [previewMode, setPreviewMode] = useState(() => {
        if (typeof window === "undefined") return false;
        return !!sessionStorage.getItem(PREVIEW_KEY);
    });

    /** Apply preview override to a user object before storing. */
    const applyPreview = (u) => {
        if (!u) return u;
        const stashed = typeof window !== "undefined" ? sessionStorage.getItem(PREVIEW_KEY) : null;
        if (stashed) return { ...u, role: "player" };
        return u;
    };

    const refresh = async () => {
        try {
            const { data } = await api.get("/auth/me");
            const next = applyPreview(data);
            setUser(next);
            return next;
        } catch {
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) localStorage.setItem("dok_token", data.token);
        sessionStorage.removeItem(PREVIEW_KEY); // any new login clears preview state
        setPreviewMode(false);
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data.token) localStorage.setItem("dok_token", data.token);
        setUser(data.user);
        return data.user;
    };

    const sendOtp = async (phone) => {
        const { data } = await api.post("/auth/otp/send", { phone });
        return data;
    };

    const verifyOtp = async (phone, code) => {
        const { data } = await api.post("/auth/otp/verify", { phone, code });
        if (data.token) localStorage.setItem("dok_token", data.token);
        setUser(data.user);
        return data.user;
    };

    // Iter 44 — accepts either { code, redirect_uri } (direct Google OAuth)
    // or a raw string session_id (legacy Emergent flow). AuthCallback drives
    // this; no other caller should hit it directly.
    const exchangeGoogleSession = async (payload) => {
        const body = typeof payload === "string" ? { session_id: payload } : payload;
        const { data } = await api.post("/auth/google/session", body);
        if (data.token) localStorage.setItem("dok_token", data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("dok_token");
        sessionStorage.removeItem(PREVIEW_KEY);
        setPreviewMode(false);
        setUser(null);
    };

    /** Enter "view as user" mode — must be called from an admin/super_admin session. */
    const previewAsUser = () => {
        if (!user || !["admin", "super_admin"].includes(user.role)) return;
        sessionStorage.setItem(PREVIEW_KEY, user.role);
        setPreviewMode(true);
        setUser({ ...user, role: "player" });
    };

    /** Exit preview — restores the admin's original role. */
    const exitPreview = () => {
        const stashed = sessionStorage.getItem(PREVIEW_KEY);
        sessionStorage.removeItem(PREVIEW_KEY);
        setPreviewMode(false);
        if (user && stashed) setUser({ ...user, role: stashed });
        else refresh();
    };

    return (
        <AuthContext.Provider value={{
            user, loading, login, register, logout, refresh,
            sendOtp, verifyOtp, exchangeGoogleSession,
            previewMode, previewAsUser, exitPreview,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
